import { GridColumnRequest, ReportGridRequest } from '@/api/services/common.dto'
import i18n from '@/lang/i18n'
import { StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.dto'
import { delayAndResponseTimesDossierService } from '@/modules/statistical-analysis-module/services/delay-and-response-times-dossier/delay-and-response-times-dossier.service'
import { StatisticalAnalysisTemporalFilterEnum } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { getSelectedRowsIds, parseGroupingParams } from '@/modules/statistical-analysis-module/shared/helpers/helpers'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum, StatisticalAnalysisDossiersDatasourceResponsesDetailTypes, StatisticalAnalysisDossiersDatasourceResponsesTypes, StatisticalAnalysisDynamicObject, StatisticalAnalysisViewDetailTableGroupingColumns } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'
import { StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo } from '@/modules/statistical-analysis-module/views/delays-and-response-time-dossier/delays-and-response-time-dossier.model'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { Nullable } from '@/shared/types/app.types'
import { AxiosResponse } from 'axios'
import { chain, reduce } from 'lodash'
import { CommonExportType } from '@/api/resources/common/shared/models/common.models'

export class StatisticalAnalysisHandlerExportTableDelaysAndResponseTimesDossier {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>
    protected selectedRows: StatisticalAnalysisDossiersDatasourceResponsesTypes[] | StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[]
    protected columns: GridColumnRequest[]

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>, selectedRows: StatisticalAnalysisDossiersDatasourceResponsesTypes[] | StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[], columns: GridColumnRequest[]) {
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
        this.selectedRows = selectedRows
        this.columns = columns
    }

    public async exportTo(exportType: CommonExportType): Promise<AxiosResponse> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        const entityIdentifiersRequest: EntityIdentifiersRequest = {
            entityIdentifiers: []
        }

        const request: ReportGridRequest = {
            sectionLabel: `${i18n.tc(sidebarFilter.trackingGeneralSelected?.label as string)} - ${i18n.tc('statisticalAnalysis.dossiers.generalStatistics.detailFor')} ${i18n.tc(sidebarFilter.trackingDetailSelected?.label as string)}`,
            columns: this.columns,
            entityIdentifiersRequest
        }
        const groupFields = (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName)
        const caseTypeId = sidebarFilter.trackingGeneralSelected?.key
        const delayType = sidebarFilter.typeDelay as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum
        const filterTimeSheetId = sidebarFilter.filterTimeSheetId!.id
        const infoDetail = sidebarFilter.timeFilter?.id as StatisticalAnalysisTemporalFilterEnum

        const selectedRows = this.selectedRows as StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed[]
        const groupValueFilterRequests: StatisticalAnalysisDynamicObject[] = reduce(selectedRows, (aggr: StatisticalAnalysisDynamicObject[], value: StatisticalAnalysisDelayAndResponseTimesDossierDetailViewTableParsed) => {
            const valueWithOmittedColumns = chain(value)
                .omitBy((_value: unknown, key: string) => key.startsWith('stats') || key.startsWith('timeSeries'))
                .omitBy((value: unknown) => value === null)
                .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                    return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
                }, {})
                .value()
            aggr.push(valueWithOmittedColumns)
            return aggr
        }, [])
        return delayAndResponseTimesDossierService.detailExportTo(
            sidebarFilter,
            request,
            groupFields,
            groupValueFilterRequests,
            caseTypeId,
            delayType,
            filterTimeSheetId,
            infoDetail,
            exportType
        )
    }

    public async exportExternal(row: StatisticalAnalysisDossiersDatasourceResponsesTypes, exportType: CommonExportType): Promise<AxiosResponse> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisDelaysAndResponseTimeDossierExternalInfo
        const entitySelected = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases
        const entityIdentifiersRequest: EntityIdentifiersRequest = {
            entityIdentifiers: getSelectedRowsIds(entitySelected, this.selectedRows as StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[])
        }

        const request: ReportGridRequest = {
            sectionLabel: `${i18n.tc(sidebarFilter.trackingGeneralSelected?.label as string)} - ${i18n.tc('statisticalAnalysis.dossiers.generalStatistics.detailFor')} ${i18n.tc(sidebarFilter.trackingDetailSelected?.label as string)}`,
            columns: this.columns,
            entityIdentifiersRequest
        }
        const caseTypeId = sidebarFilter.trackingGeneralSelected?.key
        const delayType = sidebarFilter.typeDelay as StatisticalAnalysisDelaysAndResponseTimeGeneralResponseTimeModeSelectorEnum
        const filterTimeSheetId = sidebarFilter.filterTimeSheetId!.id
        const infoDetail = sidebarFilter.timeFilter?.id as StatisticalAnalysisTemporalFilterEnum

        const groupValueFilterRequest: StatisticalAnalysisDynamicObject = chain(row)
            .omitBy((_value: unknown, key: string) => key.startsWith('stats') || key.startsWith('timeSeries'))
            .omitBy((value: unknown) => value === null)
            .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
            }, {})
            .value()
        return delayAndResponseTimesDossierService.detailExternalExportTo(
            sidebarFilter,
            request,
            groupValueFilterRequest,
            caseTypeId,
            delayType,
            filterTimeSheetId,
            infoDetail,
            exportType
        )
    }
}
